/**
 * Réglage marche/arrêt et ligne de réglage. Un seul modèle d'enregistrement
 * dans tout « Mon entreprise » et « Mon compte » : un interrupteur ou un choix
 * s'enregistre tout de suite, un nombre ou un texte en quittant le champ ; la
 * ligne dit elle-même « Enregistré » ou « Échec ».
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Check, CircleAlert } from 'lucide-react';

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export function Switch(props: {
  label: string;
  hint?: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  status?: SaveStatus | undefined;
  /** Message d'échec à la place du message par défaut. */
  error?: string | undefined;
}): ReactNode {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={props.checked}
      disabled={props.disabled || props.status === 'saving'}
      className="ui-switch"
      onClick={() => props.onChange(!props.checked)}
    >
      <span className="ui-switch__text">
        <span className="ui-switch__label">{props.label}</span>
        {props.hint ? <span className="ui-switch__hint">{props.hint}</span> : null}
        {props.status && props.status !== 'idle' ? <SaveState status={props.status} error={props.error} /> : null}
      </span>
      <span className="ui-switch__track" aria-hidden="true">
        <span />
      </span>
    </button>
  );
}

/**
 * État d'enregistrement d'un réglage. Seul sous un champ, il garde sa ligne
 * même au repos : « Enregistré » n'ajoute pas une ligne sous le champ qu'on
 * vient de quitter (la page ne saute plus). Dans une ligne de réglage ou un
 * interrupteur, il n'apparaît qu'en cours d'enregistrement.
 */
export function SaveState(props: { status: SaveStatus; error?: string | undefined }): ReactNode {
  if (props.status === 'idle') return <span className="ui-save ui-save--idle" aria-hidden="true" />;
  if (props.status === 'saving') return <span className="ui-save ui-save--saving" role="status">Enregistrement…</span>;
  if (props.status === 'saved') {
    return (
      <span className="ui-save ui-save--saved" role="status">
        <Check size={14} aria-hidden="true" /> Enregistré
      </span>
    );
  }
  if (props.status === 'error') {
    return (
      <span className="ui-save ui-save--error" role="alert">
        <CircleAlert size={14} aria-hidden="true" /> {props.error ?? 'Échec de l’enregistrement'}
      </span>
    );
  }
  return null;
}

/** Une ligne de réglage : libellé, explication, contrôle, état d'enregistrement. */
export function SettingRow(props: {
  label: string;
  hint?: ReactNode;
  children: ReactNode;
  status?: SaveStatus | undefined;
  error?: string | undefined;
}): ReactNode {
  return (
    <div className="ui-setting">
      <div className="ui-setting__text">
        <span className="ui-setting__label">{props.label}</span>
        {props.hint ? <span className="ui-setting__hint">{props.hint}</span> : null}
        {props.status && props.status !== 'idle' ? <SaveState status={props.status} error={props.error} /> : null}
      </div>
      <div className="ui-setting__control">{props.children}</div>
    </div>
  );
}

/**
 * Enregistrement d'un réglage : état affichable, « Enregistré » visible deux
 * secondes, message d'échec gardé jusqu'au prochain essai.
 */
export function useSettingSave<T>(save: (value: T) => Promise<unknown>): {
  status: SaveStatus;
  error: string | undefined;
  run: (value: T) => Promise<void>;
} {
  const [status, setStatus] = useState<SaveStatus>('idle');
  const [error, setError] = useState<string>();
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const run = useCallback(
    async (value: T) => {
      window.clearTimeout(timer.current);
      setStatus('saving');
      setError(undefined);
      try {
        await save(value);
        setStatus('saved');
        timer.current = window.setTimeout(() => setStatus('idle'), 2_000);
      } catch (failure) {
        setStatus('error');
        setError(failure instanceof Error ? failure.message : undefined);
      }
    },
    [save],
  );
  return { status, error, run };
}
